import { ArrowUpRight, NotebookPen } from "lucide-react";
import { useState } from "react";

import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { takeNotePreview } from "@domain/notebook-write-event";

interface NotebookNoteCardProps {
  /** Vault-relative note path — identity, and the deep-link payload. */
  path: string;
  /** Frontmatter title, else the filename (derived in the domain recognizer). */
  title: string;
  /** Note body, frontmatter already stripped. */
  body: string;
  /**
   * Promote to the full-page notebook with THIS note selected. Optional: chat
   * surfaces that don't wire it (workflows / studio gestation) still get the
   * in-chat preview, they just render no Open affordance rather than a button
   * that goes nowhere.
   */
  onOpen?: (path: string) => void;
}

/**
 * "New note created" — the in-chat receipt for an agent notebook write
 * (frontend-doctrine §4.17). Demonstrate, never describe: the note's own body
 * renders right here, so "that's in your notebook now" is visible rather than
 * asserted, and the reader never has to navigate to find out what was saved.
 *
 * Pure presentation (§4.4): the body arrives as a prop straight from the
 * `file_write` tool-event args — this component fetches nothing. It renders
 * through the shared `MarkdownContent` tree, so the §14.4 security rule holds
 * unchanged (no `rehype-raw`, no new `dangerouslySetInnerHTML`).
 */
export function NotebookNoteCard({ path, title, body, onOpen }: NotebookNoteCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { preview, truncated } = takeNotePreview({ body });
  const shown = expanded ? body : preview;

  return (
    <div className="my-2 overflow-hidden rounded-xl border border-black/10 bg-white/70 dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center gap-2 border-b border-black/5 px-3 py-2 dark:border-white/10">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-brand-coral/15 text-brand-coral">
          <NotebookPen className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-wider text-black/40 dark:text-white/40">
            New note created
          </span>
          <span className="block truncate font-body text-[13px] font-semibold text-black dark:text-white">
            {title}
          </span>
        </span>
        {onOpen && (
          <button
            type="button"
            onClick={() => onOpen(path)}
            title={`Open ${title} in the notebook`}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-[12px] font-bold text-black/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/5"
          >
            Open
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
          </button>
        )}
      </div>

      {shown.trim().length > 0 && (
        <div className="prose prose-sm dark:prose-invert max-w-none px-3 py-2 text-[13px]">
          <MarkdownContent content={shown} />
        </div>
      )}

      {truncated && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full border-t border-black/5 px-3 py-1.5 text-left text-[12px] font-semibold text-black/50 transition-colors hover:bg-black/5 dark:border-white/10 dark:text-white/50 dark:hover:bg-white/5"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
